/**
 * OfflineDisplayManager - Handles offline simulation display UI
 *
 * Responsibilities:
 * - Update offline progress display (miles, waves, time, speed, momentum, fuel)
 * - Fuel meter segment updates
 * - Speed breakdown tooltip
 * - Random driving status words
 * - Start simulating modal (desktop ESC key)
 */

import { getStatBreakdown } from './StatsFormatter.js';
import * as PrestigeSystem from '../game/PrestigeSystem.js';
import { getUIUpdateScheduler } from '../systems/index.js';

export class OfflineDisplayManager {
    constructor(gameState) {
        this.gameState = gameState;

        // Current driving word for status display
        this._currentDrivingWord = null;

        // Speed tooltip elements
        this._speedTooltip = null;
        this._speedTooltipHandlers = null;

        // Reference to offline simulator (set externally)
        this.offlineSimulator = null;

        // Callback for fuel depletion
        this.onFuelDepleted = null;

        // Reference to GameUI modals (set externally)
        this.gameUIModals = null;

        // Callback to unload game (for ESC modal)
        this.onUnloadGame = null;

        // Prestige badge elements
        this._prestigeBadge = null;
        this._prestigeTooltip = null;
        this._prestigeTooltipHandlers = null;
    }

    /**
     * Update the offline display with current progress
     * @param {Object} progress - Progress object from OfflineSimulator
     */
    updateDisplay(progress) {
        const milesEl = document.getElementById('opd-miles');
        const wavesEl = document.getElementById('opd-waves');
        const salvageEl = document.getElementById('opd-salvage');
        const timeEl = document.getElementById('opd-time');
        const speedEl = document.getElementById('opd-speed');
        const momentumEl = document.getElementById('opd-momentum');
        const fuelText = document.getElementById('opd-fuel-text');
        const statusEl = document.getElementById('opd-status');

        // Display effective miles (with prestige multiplier) to match what's actually added to game state
        const displayMiles = progress.effectiveMiles ?? progress.simulatedMiles;
        if (milesEl) milesEl.textContent = `+${displayMiles.toFixed(1)}`;
        if (wavesEl) wavesEl.textContent = progress.wavesFound.length.toString();
        if (salvageEl) salvageEl.textContent = `+${progress.salvageFound || 0} ⚙`;
        if (timeEl) timeEl.textContent = this.formatDuration(progress.elapsedSeconds);
        if (speedEl) speedEl.textContent = `${Math.round(progress.currentSpeed)} MPH`;
        if (momentumEl) momentumEl.textContent = `+${progress.currentMomentum.toFixed(1)}`;

        // Check if fuel is depleted - show depleted styling
        const isDepleted = progress.fuelRemaining <= 0;

        if (fuelText) {
            if (isDepleted) {
                fuelText.textContent = 'FUEL DEPLETED';
                fuelText.style.color = 'var(--cf-warning)';
            } else {
                fuelText.textContent = `${this.formatDuration(progress.fuelRemaining)} remaining`;
                fuelText.style.color = ''; // Reset color
            }
        }

        // Update status header
        if (statusEl) {
            if (isDepleted) {
                statusEl.textContent = 'OUT OF FUEL';
                statusEl.classList.remove('cf-pulse');
                statusEl.classList.add('out-of-fuel');
                this._currentDrivingWord = null;
            } else {
                // Pick a random driving word if we don't have one yet
                if (!this._currentDrivingWord || statusEl.classList.contains('out-of-fuel')) {
                    this._currentDrivingWord = this._pickRandomDrivingWord();
                }
                statusEl.textContent = this._currentDrivingWord;
                statusEl.classList.add('cf-pulse');
                statusEl.classList.remove('out-of-fuel');
            }
        }

        // Update fuel meter segments
        this.updateFuelMeter(progress.fuelRemaining, progress.fuelCapacity);
    }

    /**
     * Pick a random driving status word
     */
    _pickRandomDrivingWord() {
        const words = [
            'DRIVING',
            'VIBING',
            'CHILLING',
            'CRUISING',
            'ROLLING',
            'COASTING',
            'GROOVING',
            'FLOWING',
            'GLIDING',
            'DRIFTING'
        ];
        return words[Math.floor(Math.random() * words.length)];
    }

    /**
     * Update segmented fuel meter
     */
    updateFuelMeter(remaining, capacity) {
        const meter = document.getElementById('opd-fuel-meter');
        if (!meter) return;

        const segments = meter.querySelectorAll('.cf-meter-segment');
        const percent = remaining / capacity;
        const filledCount = Math.ceil(percent * segments.length);

        segments.forEach((segment, i) => {
            const isFilled = i < filledCount;
            const isActive = i === filledCount - 1;

            segment.classList.toggle('filled', isFilled);
            segment.classList.toggle('active', isFilled && isActive);
        });

        // Remove depleted state if fuel is available
        if (remaining > 0) {
            meter.classList.remove('depleted');
        }
    }

    /**
     * Format duration for display
     */
    formatDuration(seconds) {
        if (seconds < 60) return `${Math.floor(seconds)}s`;
        if (seconds < 3600) {
            const mins = Math.floor(seconds / 60);
            const secs = Math.floor(seconds % 60);
            return `${mins}:${secs.toString().padStart(2, '0')}`;
        }
        const hours = Math.floor(seconds / 3600);
        const mins = Math.floor((seconds % 3600) / 60);
        return `${hours}h ${mins}m`;
    }

    /**
     * Handle fuel depletion during offline simulation
     * Updates all UI elements to show depleted state
     */
    handleFuelDepleted() {
        // Update fuel text
        const fuelText = document.getElementById('opd-fuel-text');
        if (fuelText) {
            fuelText.textContent = 'FUEL DEPLETED';
            fuelText.style.color = 'var(--cf-warning)';
        }

        // Add blinking animation to fuel meter
        const fuelMeter = document.getElementById('opd-fuel-meter');
        if (fuelMeter) {
            fuelMeter.classList.add('depleted');
        }

        // Update status header to OUT OF FUEL
        const statusEl = document.getElementById('opd-status');
        if (statusEl) {
            statusEl.textContent = 'OUT OF FUEL';
            statusEl.classList.remove('cf-pulse');
            statusEl.classList.add('out-of-fuel');
        }
        this._currentDrivingWord = null; // Reset so we pick a new word when driving resumes

        // Call external callback if set
        if (this.onFuelDepleted) {
            this.onFuelDepleted();
        }
    }

    /**
     * Show the offline display in depleted state (no fuel, not simulating)
     * Used on startup when user has no fuel
     * @param {Object} progress - Optional progress object to display (preserves offline progress)
     */
    showDepletedState(progress = null) {
        // Ensure display is visible
        const display = document.getElementById('offline-progress-display');
        if (display) {
            display.classList.add('active');
        }

        // If progress provided, show it instead of resetting to 0
        // This preserves offline progress when fuel ran out during offline simulation
        const hasProgress = progress && (
            (progress.effectiveMiles ?? progress.simulatedMiles) > 0 ||
            progress.wavesFound?.length > 0 ||
            progress.elapsedSeconds > 0
        );
        if (hasProgress) {
            // Display the accumulated progress
            const milesEl = document.getElementById('opd-miles');
            const wavesEl = document.getElementById('opd-waves');
            const salvageEl = document.getElementById('opd-salvage');
            const timeEl = document.getElementById('opd-time');
            const speedEl = document.getElementById('opd-speed');
            const momentumEl = document.getElementById('opd-momentum');

            // Display effective miles (with prestige multiplier)
            const displayMiles = progress.effectiveMiles ?? progress.simulatedMiles;
            if (milesEl) milesEl.textContent = `+${displayMiles.toFixed(1)}`;
            if (wavesEl) wavesEl.textContent = progress.wavesFound?.length?.toString() || '0';
            if (salvageEl) salvageEl.textContent = `+${progress.salvageFound || 0} ⚙`;
            if (timeEl) timeEl.textContent = this.formatDuration(progress.elapsedSeconds || 0);
            if (speedEl) speedEl.textContent = '0 MPH'; // Speed is 0 when fuel depleted
            if (momentumEl) momentumEl.textContent = '+0.0'; // Momentum lost when stopped

            console.log('[OfflineDisplay] Showing depleted state with progress:', {
                miles: displayMiles?.toFixed(1),
                waves: progress.wavesFound?.length,
                salvage: progress.salvageFound || 0,
                elapsed: progress.elapsedSeconds?.toFixed(1) + 's'
            });
        } else {
            // No progress to show, reset to slot data
            this.resetToSlot();
        }

        // Apply depleted styling to fuel display
        const fuelText = document.getElementById('opd-fuel-text');
        if (fuelText) {
            fuelText.textContent = 'FUEL DEPLETED';
            fuelText.style.color = 'var(--cf-warning)';
        }

        const fuelMeter = document.getElementById('opd-fuel-meter');
        if (fuelMeter) {
            fuelMeter.classList.add('depleted');
        }

        const statusEl = document.getElementById('opd-status');
        if (statusEl) {
            statusEl.textContent = 'OUT OF FUEL';
            statusEl.classList.remove('cf-pulse');
            statusEl.classList.add('out-of-fuel');
        }
        this._currentDrivingWord = null; // Reset so we pick a new word when driving resumes

        console.log('[OfflineDisplay] Showing depleted state');
    }

    /**
     * Reset the offline display to show the current slot's data (no simulator running)
     * Called when switching save slots to clear stale simulator display
     */
    resetToSlot() {
        const state = this.gameState.state;
        const fuel = state?.fuel ?? 1800;
        const fuelCapacity = this.gameState.getFuelTankCapacity();

        // Reset all display elements to initial/slot state
        const milesEl = document.getElementById('opd-miles');
        const wavesEl = document.getElementById('opd-waves');
        const salvageEl = document.getElementById('opd-salvage');
        const timeEl = document.getElementById('opd-time');
        const speedEl = document.getElementById('opd-speed');
        const momentumEl = document.getElementById('opd-momentum');
        const fuelText = document.getElementById('opd-fuel-text');
        const statusEl = document.getElementById('opd-status');

        if (milesEl) milesEl.textContent = '+0.0';
        if (wavesEl) wavesEl.textContent = '0';
        if (salvageEl) salvageEl.textContent = '+0 ⚙';
        if (timeEl) timeEl.textContent = '0:00';
        if (speedEl) speedEl.textContent = '0 MPH';
        if (momentumEl) {
            const exitMomentum = state?.session?.exitMomentum || 0;
            momentumEl.textContent = `+${exitMomentum.toFixed(1)}`;
        }
        if (fuelText) {
            fuelText.textContent = `${this.formatDuration(fuel)} remaining`;
            fuelText.style.color = ''; // Reset color
        }
        if (statusEl) {
            statusEl.textContent = 'READY';
            statusEl.classList.remove('cf-pulse', 'out-of-fuel');
        }

        // Update fuel meter with current slot's fuel
        this.updateFuelMeter(fuel, fuelCapacity);

        // Remove depleted state if fuel available
        const fuelMeter = document.getElementById('opd-fuel-meter');
        if (fuelMeter && fuel > 0) {
            fuelMeter.classList.remove('depleted');
        }

        console.log('[OfflineDisplay] Reset to slot data:', {
            fuel,
            exitMomentum: state?.session?.exitMomentum || 0
        });
    }

    /**
     * Set up speed breakdown tooltip for offline display
     */
    setupSpeedTooltip() {
        const speedInfo = document.getElementById('opd-speed-info');
        if (!speedInfo) return;

        // Cleanup previous tooltip if it exists
        if (this._speedTooltip) {
            this._speedTooltip.remove();
            this._speedTooltip = null;
        }
        if (this._speedTooltipHandlers) {
            const h = this._speedTooltipHandlers;
            speedInfo.removeEventListener('mouseenter', h.show);
            speedInfo.removeEventListener('mouseleave', h.hide);
            speedInfo.removeEventListener('touchend', h.touchToggle);
            speedInfo.removeEventListener('click', h.clickToggle);
            document.removeEventListener('click', h.outsideClick);
            document.removeEventListener('touchend', h.outsideTouchend);
            this._speedTooltipHandlers = null;
        }

        // Create tooltip element
        const tooltip = document.createElement('div');
        tooltip.className = 'cf-car-tooltip opd-speed-tooltip';
        tooltip.style.display = 'none';
        document.body.appendChild(tooltip);

        // Update interval for real-time values
        let updateInterval = null;

        // Helper to create tooltip row
        const createSpeedRow = (label, value, isPositive = false, isTotal = false) => {
            const row = document.createElement('div');
            row.className = isTotal ? 'cf-car-tooltip-total' : 'cf-car-tooltip-row';
            const labelSpan = document.createElement('span');
            labelSpan.className = 'cf-car-tooltip-label';
            labelSpan.textContent = label;
            row.appendChild(labelSpan);
            const valueSpan = document.createElement('span');
            valueSpan.className = `cf-car-tooltip-value${isPositive ? ' positive' : ''}`;
            valueSpan.textContent = value;
            row.appendChild(valueSpan);
            return row;
        };

        const updateTooltipContent = () => {
            // Get breakdown data
            const carConfig = this.gameState.carsConfig[this.gameState.state.currentCarId];
            const car = this.gameState.getCurrentCar();
            const level = car.upgradeLevels.baseSpeed || 0;
            const breakdown = getStatBreakdown('baseSpeed', level, carConfig, this.gameState);

            // Get current momentum from simulator (updates in real-time)
            const momentum = this.offlineSimulator?.currentMomentum || 0;

            // Clear and rebuild tooltip content
            while (tooltip.firstChild) tooltip.removeChild(tooltip.firstChild);

            const formatVal = (v) => v.toFixed(1);

            // Title
            const title = document.createElement('div');
            title.className = 'cf-car-tooltip-title';
            title.textContent = 'SPEED BREAKDOWN';
            tooltip.appendChild(title);

            // Base value
            tooltip.appendChild(createSpeedRow('Base', `${formatVal(breakdown.baseValue)} MPH`));

            // Upgrades
            if (breakdown.upgradeBonus !== 0) {
                tooltip.appendChild(createSpeedRow(`Upgrades (LV${breakdown.level})`, `+${formatVal(breakdown.upgradeBonus)} MPH`, true));
            }

            // Tier bonus
            if (breakdown.tierBonus !== 0) {
                const tierName = this.gameState.getCarTier();
                const tierCapitalized = tierName.charAt(0).toUpperCase() + tierName.slice(1);
                tooltip.appendChild(createSpeedRow(`${tierCapitalized} Bonus`, `+${formatVal(breakdown.tierBonus)} MPH`, true));
            }

            // Prestige
            if (breakdown.prestigeBonusValue !== 0) {
                const prestigePercent = (breakdown.prestigeBonus * 100).toFixed(1);
                tooltip.appendChild(createSpeedRow(`Prestige (${prestigePercent}%)`, `+${formatVal(breakdown.prestigeBonusValue)} MPH`, true));
            }

            // Ascension bonus
            if (breakdown.ab !== 0) {
                tooltip.appendChild(createSpeedRow('Ascension Bonus', `+${formatVal(breakdown.ab)} MPH`, true));
            }

            // Item bonuses
            if (breakdown.itemAddBonus !== 0) {
                tooltip.appendChild(createSpeedRow('Items', `+${formatVal(breakdown.itemAddBonus)} MPH`, true));
            }
            if (breakdown.itemMultiplyBonus !== 0) {
                const multPercent = Math.round((breakdown.itemMultiplier - 1) * 100);
                tooltip.appendChild(createSpeedRow(`Items (${multPercent}%)`, `+${formatVal(breakdown.itemMultiplyBonus)} MPH`, true));
            }

            // Collection milestone bonus
            if (breakdown.collectionBonus && breakdown.collectionBonus !== 0) {
                tooltip.appendChild(createSpeedRow('Collection', `+${formatVal(breakdown.collectionBonus)} MPH`, true));
            }

            // Divider before momentum
            const divider = document.createElement('div');
            divider.style.cssText = 'border-top: 1px solid var(--cf-divider); margin: var(--cf-space-sm) 0;';
            tooltip.appendChild(divider);

            // Momentum (dynamic - updates in real-time)
            tooltip.appendChild(createSpeedRow('Momentum', `+${formatVal(momentum)} MPH`, true));

            // Total
            const totalSpeed = breakdown.total + momentum;
            tooltip.appendChild(createSpeedRow('TOTAL', `${formatVal(totalSpeed)} MPH`, false, true));
        };

        const showTooltip = () => {
            // Initial content update
            updateTooltipContent();

            tooltip.style.display = 'block';
            tooltip.style.position = 'fixed';
            tooltip.style.transform = 'none';

            // Get dimensions after content is set
            const rect = speedInfo.getBoundingClientRect();
            const tooltipRect = tooltip.getBoundingClientRect();
            const padding = 8;
            const gap = 8;

            // Start with centered above the anchor
            let left = rect.left + rect.width / 2 - tooltipRect.width / 2;
            let top = rect.top - tooltipRect.height - gap;

            // Clamp horizontal position to viewport
            const maxLeft = window.innerWidth - tooltipRect.width - padding;
            const minLeft = padding;
            left = Math.max(minLeft, Math.min(maxLeft, left));

            // If tooltip would go above viewport, show it below instead
            if (top < padding) {
                top = rect.bottom + gap;
            }

            // Clamp vertical position to viewport
            const maxTop = window.innerHeight - tooltipRect.height - padding;
            top = Math.max(padding, Math.min(maxTop, top));

            tooltip.style.left = `${left}px`;
            tooltip.style.top = `${top}px`;

            // Start real-time updates (every 100ms to match simulator tick)
            if (updateInterval) clearInterval(updateInterval);
            updateInterval = setInterval(updateTooltipContent, 100);
        };

        const hideTooltip = () => {
            tooltip.style.display = 'none';
            // Stop real-time updates
            if (updateInterval) {
                clearInterval(updateInterval);
                updateInterval = null;
            }
        };

        // Touch/click toggle - use touchend for immediate mobile response
        let touchHandled = false;

        // Create named handlers that can be removed later
        const handlers = {
            show: showTooltip,
            hide: hideTooltip,
            touchToggle: (e) => {
                e.preventDefault();
                e.stopPropagation();
                touchHandled = true;
                const isVisible = tooltip.style.display === 'block';
                if (isVisible) hideTooltip();
                else showTooltip();
            },
            clickToggle: (e) => {
                // Skip if touch already handled this
                if (touchHandled) {
                    touchHandled = false;
                    return;
                }
                e.stopPropagation();
                const isVisible = tooltip.style.display === 'block';
                if (isVisible) hideTooltip();
                else showTooltip();
            },
            outsideClick: (e) => {
                if (tooltip.style.display === 'block' &&
                    !tooltip.contains(e.target) &&
                    !speedInfo.contains(e.target)) {
                    hideTooltip();
                }
            },
            outsideTouchend: (e) => {
                if (tooltip.style.display === 'block' &&
                    !tooltip.contains(e.target) &&
                    !speedInfo.contains(e.target)) {
                    hideTooltip();
                }
            }
        };

        // Desktop hover
        speedInfo.addEventListener('mouseenter', handlers.show);
        speedInfo.addEventListener('mouseleave', handlers.hide);

        // Touch/click toggle
        speedInfo.addEventListener('touchend', handlers.touchToggle);
        speedInfo.addEventListener('click', handlers.clickToggle);

        // Close tooltip when clicking/touching outside
        document.addEventListener('click', handlers.outsideClick);
        document.addEventListener('touchend', handlers.outsideTouchend);

        // Store references for cleanup
        this._speedTooltip = tooltip;
        this._speedTooltipHandlers = handlers;
    }

    /**
     * Set up prestige badge with tooltip in the offline display
     * Shows prestige level and bonuses when tapped/hovered
     */
    setupPrestigeBadge() {
        const display = document.getElementById('offline-progress-display');
        if (!display) {
            console.log('[OfflineDisplay] setupPrestigeBadge: display element not found');
            return;
        }

        // Cleanup previous badge if it exists
        if (this._prestigeBadge) {
            this._prestigeBadge.remove();
            this._prestigeBadge = null;
        }
        if (this._prestigeTooltip) {
            this._prestigeTooltip.remove();
            this._prestigeTooltip = null;
        }
        if (this._badgePositionHandler) {
            window.removeEventListener('resize', this._badgePositionHandler);
            this._badgePositionHandler = null;
        }
        // Unregister badge position update from UIUpdateScheduler (Phase 6 TMR-02)
        if (this._badgePositionUpdateId) {
            const scheduler = getUIUpdateScheduler();
            scheduler.unregister(this._badgePositionUpdateId);
            this._badgePositionUpdateId = null;
        }
        if (this._prestigeTooltipHandlers) {
            // Handlers were attached to the old badge which is now removed
            this._prestigeTooltipHandlers = null;
        }

        // Only show if prestige level > 0
        const prestigeInfo = this.gameState.state.prestige || {};
        console.log('[OfflineDisplay] setupPrestigeBadge: prestigeInfo =', prestigeInfo);
        if (!prestigeInfo.level || prestigeInfo.level <= 0) {
            console.log('[OfflineDisplay] setupPrestigeBadge: no prestige level, skipping');
            return;
        }

        // Create badge element - position relative to display using fixed positioning
        const badge = document.createElement('div');
        badge.id = 'opd-prestige-badge';
        badge.textContent = `PRESTIGE ${prestigeInfo.level}`;

        const tierColor = PrestigeSystem.getPrestigeTierColor(prestigeInfo.level);
        badge.style.cssText = `
            position: fixed;
            padding: 2px 6px;
            font-size: 9px;
            font-weight: bold;
            letter-spacing: 0.5px;
            border: 1px solid ${tierColor};
            border-radius: 3px;
            background: rgba(0, 0, 0, 0.8);
            color: ${tierColor};
            cursor: pointer;
            z-index: 10000;
            user-select: none;
            -webkit-user-select: none;
            touch-action: manipulation;
            pointer-events: auto;
        `;

        // Append to body instead of display to avoid overflow issues
        document.body.appendChild(badge);
        this._prestigeBadge = badge;

        // Position the badge relative to the display
        const updateBadgePosition = () => {
            // Sync visibility with display - check both dimensions AND active class
            const hasActiveClass = display.classList.contains('active');
            const displayVisible = hasActiveClass && display.offsetWidth > 0 && display.offsetHeight > 0;

            if (!displayVisible) {
                badge.style.display = 'none';
                // Also hide tooltip if badge is hidden
                if (this._prestigeTooltip) {
                    this._prestigeTooltip.style.display = 'none';
                }
                return; // Don't update position if hidden
            }

            badge.style.display = 'block';
            const displayRect = display.getBoundingClientRect();
            // Position in top-right of the display
            badge.style.top = `${displayRect.top + 8}px`;
            badge.style.left = `${displayRect.right - badge.offsetWidth - 8}px`;
            badge.style.right = 'auto';
        };

        // Initial position after badge is in DOM
        requestAnimationFrame(() => {
            updateBadgePosition();
            console.log('[OfflineDisplay] Badge positioned, display rect:', display.getBoundingClientRect());
        });

        // Update on resize and periodically using UIUpdateScheduler (Phase 6 TMR-02)
        this._badgePositionHandler = updateBadgePosition;
        window.addEventListener('resize', updateBadgePosition);
        this._badgePositionUpdateId = 'badge-position-update';
        const scheduler = getUIUpdateScheduler();
        scheduler.register(this._badgePositionUpdateId, updateBadgePosition, { interval: 200 });

        console.log('[OfflineDisplay] Created prestige badge for level', prestigeInfo.level);

        // Create tooltip
        const tooltip = document.createElement('div');
        tooltip.className = 'cf-car-tooltip opd-prestige-tooltip';
        tooltip.style.display = 'none';
        document.body.appendChild(tooltip);
        this._prestigeTooltip = tooltip;

        // Helper to create tooltip row for prestige
        const createPrestigeRow = (label, value, isPositive = false) => {
            const row = document.createElement('div');
            row.className = 'cf-car-tooltip-row';
            const labelSpan = document.createElement('span');
            labelSpan.className = 'cf-car-tooltip-label';
            labelSpan.textContent = label;
            row.appendChild(labelSpan);
            if (value) {
                const valueSpan = document.createElement('span');
                valueSpan.className = `cf-car-tooltip-value${isPositive ? ' positive' : ''}`;
                valueSpan.textContent = value;
                row.appendChild(valueSpan);
            }
            return row;
        };

        // Build tooltip content
        const updateTooltipContent = () => {
            while (tooltip.firstChild) tooltip.removeChild(tooltip.firstChild);

            const title = document.createElement('div');
            title.className = 'cf-car-tooltip-title';
            title.textContent = `PRESTIGE ${prestigeInfo.level} BONUSES`;
            tooltip.appendChild(title);

            // Mile Earning Multiplier
            const mileBonus = prestigeInfo.mileEarningBonus || 0;
            tooltip.appendChild(createPrestigeRow('Mile Earning', PrestigeSystem.formatBonusMultiplier(mileBonus), true));

            // Skill bonuses (only show non-zero)
            const skillBonuses = prestigeInfo.skillBonuses || {};
            let hasSkillBonuses = false;

            Object.entries(skillBonuses).forEach(([skillId, bonus]) => {
                if (bonus > 0) {
                    hasSkillBonuses = true;
                    const skillInfo = PrestigeSystem.SELECTABLE_SKILLS[skillId];
                    const skillName = skillInfo ? skillInfo.name : skillId;
                    const displaySign = skillId === 'cruiseCooldown' ? '-' : '+';
                    tooltip.appendChild(createPrestigeRow(skillName, `${displaySign}${(bonus * 100).toFixed(1)}%`, true));
                }
            });

            if (!hasSkillBonuses) {
                const noSkillRow = document.createElement('div');
                noSkillRow.className = 'cf-car-tooltip-row';
                noSkillRow.style.marginTop = 'var(--cf-space-sm)';
                const noSkillLabel = document.createElement('span');
                noSkillLabel.className = 'cf-car-tooltip-label';
                noSkillLabel.style.color = 'var(--cf-text-tertiary)';
                noSkillLabel.textContent = 'No skill bonuses selected yet';
                noSkillRow.appendChild(noSkillLabel);
                tooltip.appendChild(noSkillRow);
            }
        };

        updateTooltipContent();

        const showTooltip = () => {
            // Hide any other open tooltips first
            document.querySelectorAll('.cf-car-tooltip').forEach(t => {
                if (t !== tooltip) t.style.display = 'none';
            });

            tooltip.style.display = 'block';
            tooltip.style.position = 'fixed';
            tooltip.style.zIndex = '10003';
            tooltip.style.transform = 'none';

            // Need to get dimensions after display:block
            requestAnimationFrame(() => {
                const rect = badge.getBoundingClientRect();
                const tooltipRect = tooltip.getBoundingClientRect();
                const padding = 8;
                const gap = 8;

                // Position below the badge
                let left = rect.left;
                let top = rect.bottom + gap;

                // If tooltip would go off right edge, align to right of badge
                if (left + tooltipRect.width > window.innerWidth - padding) {
                    left = rect.right - tooltipRect.width;
                }

                // Clamp horizontal position
                left = Math.max(padding, Math.min(window.innerWidth - tooltipRect.width - padding, left));

                // If tooltip would go off bottom, position above badge
                if (top + tooltipRect.height > window.innerHeight - padding) {
                    top = rect.top - tooltipRect.height - gap;
                }

                // Clamp vertical position
                top = Math.max(padding, top);

                tooltip.style.left = `${left}px`;
                tooltip.style.top = `${top}px`;

                console.log('[OfflineDisplay] Tooltip positioned at:', { left, top, tooltipRect });
            });
        };

        const hideTooltip = () => {
            tooltip.style.display = 'none';
        };

        const handlers = {
            show: () => {
                console.log('[OfflineDisplay] Prestige badge: show tooltip');
                showTooltip();
            },
            hide: () => {
                console.log('[OfflineDisplay] Prestige badge: hide tooltip');
                hideTooltip();
            },
            toggle: (e) => {
                console.log('[OfflineDisplay] Prestige badge: toggle clicked');
                e.stopPropagation();
                const isVisible = tooltip.style.display === 'block';
                if (isVisible) {
                    console.log('[OfflineDisplay] Prestige badge: hiding');
                    hideTooltip();
                } else {
                    console.log('[OfflineDisplay] Prestige badge: showing');
                    showTooltip();
                }
            },
            outsideClick: (e) => {
                if (tooltip.style.display === 'block' &&
                    !tooltip.contains(e.target) &&
                    !badge.contains(e.target)) {
                    hideTooltip();
                }
            }
        };

        // Desktop hover
        badge.addEventListener('mouseenter', handlers.show);
        badge.addEventListener('mouseleave', handlers.hide);

        // Click toggle (works for both desktop and mobile)
        badge.addEventListener('click', handlers.toggle);

        // Close tooltip when clicking outside
        document.addEventListener('click', handlers.outsideClick);

        this._prestigeTooltipHandlers = handlers;
    }

    /**
     * Hide the prestige badge (called when offline display is hidden)
     */
    hidePrestigeBadge() {
        if (this._prestigeBadge) {
            this._prestigeBadge.style.display = 'none';
        }
        if (this._prestigeTooltip) {
            this._prestigeTooltip.style.display = 'none';
        }
    }

    /**
     * Show "Start Simulating?" modal (desktop ESC key)
     */
    showStartSimulatingModal() {
        if (!this.gameUIModals) return;

        const modal = this.gameUIModals.create({
            className: 'start-simulating-modal cf-ui',
            closeOnBackdrop: true,
            maxWidth: '340px'
        });

        // Build modal content with DOM
        const wrapper = document.createElement('div');
        wrapper.style.cssText = 'text-align: center; padding: var(--cf-space-lg);';

        const title = document.createElement('h2');
        title.className = 'cf-title';
        title.style.cssText = 'font-size: 16px; margin-bottom: var(--cf-space-md); color: var(--cf-text-primary);';
        title.textContent = 'STANDBY?';
        wrapper.appendChild(title);

        const message = document.createElement('p');
        message.className = 'cf-muted';
        message.style.cssText = 'margin-bottom: var(--cf-space-lg); font-size: var(--cf-font-size-sm); line-height: 1.5;';
        message.textContent = 'Your car will continue driving in standby mode. Return anytime to collect your progress.';
        wrapper.appendChild(message);

        const btnContainer = document.createElement('div');
        btnContainer.style.cssText = 'display: flex; gap: var(--cf-space-md); justify-content: center;';

        const yesBtn = document.createElement('button');
        yesBtn.className = 'cf-button confirm-yes';
        yesBtn.style.cssText = 'background: rgba(100, 180, 255, 0.15); border-color: rgba(100, 180, 255, 0.5); color: rgba(150, 200, 255, 1);';
        yesBtn.textContent = 'STANDBY';
        btnContainer.appendChild(yesBtn);

        const noBtn = document.createElement('button');
        noBtn.className = 'cf-button confirm-no';
        noBtn.textContent = 'CANCEL';
        btnContainer.appendChild(noBtn);

        wrapper.appendChild(btnContainer);
        modal.container.appendChild(wrapper);

        yesBtn.onclick = () => {
            modal.close();
            // Call external unload callback
            if (this.onUnloadGame) {
                this.onUnloadGame();
            }
        };

        noBtn.onclick = () => modal.close();
        noBtn.focus();
    }

    /**
     * Cleanup resources
     */
    dispose() {
        if (this._speedTooltip) {
            this._speedTooltip.remove();
            this._speedTooltip = null;
        }
        this._speedTooltipHandlers = null;

        if (this._prestigeBadge) {
            this._prestigeBadge.remove();
            this._prestigeBadge = null;
        }
        if (this._prestigeTooltip) {
            this._prestigeTooltip.remove();
            this._prestigeTooltip = null;
        }
        if (this._badgePositionHandler) {
            window.removeEventListener('resize', this._badgePositionHandler);
            this._badgePositionHandler = null;
        }
        // Unregister badge position update from UIUpdateScheduler (Phase 6 TMR-02)
        if (this._badgePositionUpdateId) {
            const scheduler = getUIUpdateScheduler();
            scheduler.unregister(this._badgePositionUpdateId);
            this._badgePositionUpdateId = null;
        }
        this._prestigeTooltipHandlers = null;
    }
}
